import { motion } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Play, Pause, GripVertical, MoreVertical, Pencil, Trash2, FileAudio, Scissors, Share2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlayer } from '../contexts/PlayerContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const ExcerptCard = ({ 
  excerpt, 
  surah, 
  excerptList,
  onEdit, 
  onEditAudio,
  onDelete,
  onReplace,
  index,
  showActions = true 
}) => {
  const { t } = useLanguage();
  const { currentExcerpt, isPlaying, playExcerpt, togglePlay } = usePlayer();

  const isCurrentlyPlaying = currentExcerpt?.id === excerpt.id && isPlaying;
  const isCurrent = currentExcerpt?.id === excerpt.id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: excerpt.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handlePlay = () => {
    if (isCurrent) {
      togglePlay();
    } else {
      playExcerpt(excerpt, surah, excerptList);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}?surah=${surah.id}&excerpt=${excerpt.id}`;
    const shareTitle = `${surah.name_arabic} - ${excerpt.title || `صفحة ${excerpt.page_number}`}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `استمع إلى: ${shareTitle}`,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('تم نسخ الرابط');
        }
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('تم نسخ الرابط');
    }
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: isDragging ? 0.5 : 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`
        group relative rounded-2xl p-4
        bg-card border border-border
        ${isCurrent ? 'ring-2 ring-primary border-primary' : ''}
        ${isDragging ? 'shadow-lg z-50' : ''}
        hover:border-primary/30
        transition-all duration-200
      `}
      data-testid={`excerpt-card-${excerpt.id}`}
      data-dragging={isDragging}
    >
      <div className="flex items-center gap-4">
        {/* Drag Handle - Only for admin */}
        {showActions && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`excerpt-drag-${excerpt.id}`}
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
        )}

        {/* Play Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handlePlay}
          className={`
            rounded-full h-12 w-12 shrink-0
            ${isCurrent ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-muted hover:bg-muted/80'}
          `}
          data-testid={`excerpt-play-${excerpt.id}`}
        >
          {isCurrentlyPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5" />
          )}
        </Button>

        {/* Info */}
        <div className="flex-1 min-w-0 text-right">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-foreground">
              {excerpt.title || `${t('pageNumber')} ${excerpt.page_number}`}
            </span>
            {isCurrent && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center gap-1"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
              </motion.div>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
            <span className="flex items-center gap-1">
              <FileAudio className="h-3 w-3" />
              {formatTime(excerpt.duration)}
            </span>
            <span>{t('wajhNumber')}: {excerpt.page_number}</span>
            {excerpt.mushaf_page_number && (
              <span>{t('mushafPageNumber')}: {excerpt.mushaf_page_number}</span>
            )}
          </div>
        </div>

        {/* Share Button - visible for everyone */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleShare}
          className="h-8 w-8 rounded-full shrink-0 text-muted-foreground"
          data-testid={`excerpt-share-${excerpt.id}`}
        >
          <Share2 className="h-4 w-4" />
        </Button>

        {/* Actions Menu - Only for admin */}
        {showActions && onEdit && onDelete && (
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`excerpt-menu-${excerpt.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem 
                  onClick={onEdit}
                  data-testid={`excerpt-edit-${excerpt.id}`}
                >
                  <Pencil className="w-4 h-4 ml-2" />
                  {t('editExcerpt')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={onEditAudio}
                  data-testid={`excerpt-edit-audio-${excerpt.id}`}
                >
                  <Scissors className="w-4 h-4 ml-2" />
                  {t('adjustAudio')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={onReplace}
                  data-testid={`excerpt-replace-${excerpt.id}`}
                >
                  <FileAudio className="w-4 h-4 ml-2" />
                  {t('replaceAudio')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive"
                  data-testid={`excerpt-delete-${excerpt.id}`}
                >
                  <Trash2 className="w-4 h-4 ml-2" />
                  {t('deleteExcerpt')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </motion.div>
  );
};
